import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { UserDataContext } from '../../context/UserDataContext';
import CasinoIcon from '@mui/icons-material/Casino';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';

// Custom theme colors
const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
  lightGreen: '#E8F5E9',
};

const API_BASE_URL = 'https://bosscrowns-api-a228488a1e46.herokuapp.com/bingo';

const CreateGame = () => {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isAdmin = true; // Example: Check if user is admin

  // Verify password
  const verifyPassword = async (pwd) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      const data = await response.json();
      if (data.success) {
        sessionStorage.setItem('adminPassword', pwd); // Store password in sessionStorage
        setPasswordModalOpen(false);
        setPasswordError(null);
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem('adminPassword'); // Clear invalid password
        setPasswordError(data.message || 'Invalid password');
      }
    } catch (error) {
      sessionStorage.removeItem('adminPassword'); // Clear on server error
      setPasswordError('Server error');
    } finally {
      setIsLoading(false);
    }
  };

  // Check stored password on mount
  useEffect(() => {
    const storedPassword = sessionStorage.getItem('adminPassword');
    if (storedPassword) {
      verifyPassword(storedPassword); // Auto-verify stored password
    } else {
      setPasswordModalOpen(true); // Open modal if no stored password
    }
  }, []);

  const createGame = async () => {
    if (!isAdmin || !isAuthenticated) {
      alert('Only authenticated admins can create a game');
      return;
    }
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to create game');
      const { pin } = await response.json();
      console.log('Created game with PIN:', pin);
      navigate(`/bingo/game-master/${pin}`);
    } catch (err) {
      alert(`Error creating game: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 5, bgcolor: theme.background }}>
        <Card sx={{ boxShadow: 3, bgcolor: theme.background }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
              <LockIcon sx={{ color: theme.primary, mr: 1 }} />
              <Typography variant="h6" sx={{ color: theme.text }}>
                Access Denied: Only admins can access this page.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 5, bgcolor: theme.background }}>
      {/* Password Modal */}
      <Dialog open={passwordModalOpen} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.primary, color: theme.background, display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1 }} />
          Admin Authentication
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.background, pt: 2 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <Typography variant="body1" sx={{ color: theme.text, mb: 2 }}>
            Please enter the admin password to access game creation.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoFocus
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.primary,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.background }}>
          <Button
            onClick={() => navigate('/')}
            variant="outlined"
            disabled={isLoading}
            sx={{ color: theme.text, borderColor: theme.text }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => verifyPassword(password)}
            variant="contained"
            disabled={isLoading || !password}
            sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      {!passwordModalOpen && (
        <Card sx={{ boxShadow: 3, bgcolor: theme.background, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <CasinoIcon sx={{ fontSize: 60, color: theme.primary, mb: 2 }} />
              <Typography variant="h4" sx={{ color: theme.text, mb: 2, fontWeight: 'bold' }}>
                Create a New Bingo Game
              </Typography>
              <Typography variant="body1" sx={{ color: theme.text, mb: 4, maxWidth: 600 }}>
                Start a new bingo game session as the Game Master. A unique PIN will be generated for players to join.
              </Typography>

              {/* Instructions Section */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 4,
                  bgcolor: theme.lightGreen,
                  borderRadius: 2,
                  width: '100%',
                  maxWidth: 600,
                }}
              >
                <Typography variant="h6" sx={{ color: theme.text, mb: 2, display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, color: theme.primary }} />
                  How to Create a Game
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Click the 'Create New Game' button below."
                      secondary="This will generate a unique game PIN."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Share the PIN with players."
                      secondary="Players can join the game using this PIN."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Manage the game as the Game Master."
                      secondary="Call numbers, enable auto-marking, or start auto-calling from the Game Master dashboard."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                </List>
              </Paper>

              <Button
                variant="contained"
                onClick={createGame}
                size="large"
                startIcon={<CasinoIcon />}
                disabled={isLoading}
                sx={{
                  bgcolor: theme.primary,
                  '&:hover': { bgcolor: theme.secondary },
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  borderRadius: 8,
                }}
              >
                Create New Game
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default CreateGame;